import { f0FormField, CustomFieldRenderProps, F0Field } from '@factorialco/f0-react'
import { z } from 'zod'

import { EmployeeSelectorV2 } from '.'
import { CustomFiltersWithNoExtraFilters, EmployeeSelectorV2Props } from './types'
import { EntitySelectorV2Messages } from 'modules/core/components/EntitySelectorV2/types'

// Exclude placeholder from messages since it's provided by f0FormField's render props
type FieldConfigMessages = Omit<EntitySelectorV2Messages, 'placeholder'>

type FieldConfig = Partial<
  Pick<EmployeeSelectorV2Props, 'options' | 'forcedFilters' | 'hint' | 'useAccessId'>
> & {
  filters?: CustomFiltersWithNoExtraFilters
  messages?: FieldConfigMessages
}

type BaseEmployeeSelectorV2FormFieldParams = {
  label: string
  section: string
  helpText?: string
  fieldConfig?: FieldConfig
  renderIf?: F0Field['renderIf']
}

const createOptionalSingleSchema = () => z.string().optional()

const optionalSingleEmployeeSelectorV2FormField = ({
  label,
  section,
  helpText,
  fieldConfig,
  renderIf,
}: BaseEmployeeSelectorV2FormFieldParams) => {
  const schema = createOptionalSingleSchema()

  return f0FormField<typeof schema, FieldConfig>(schema, {
    label,
    section,
    helpText,
    fieldType: 'custom',
    fieldConfig: fieldConfig ?? {},
    renderIf,
    render: ({
      value,
      onChange,
      disabled,
      placeholder,
      config,
    }: CustomFieldRenderProps<string | undefined, FieldConfig>) => {
      const { forcedFilters, options, hint, messages, useAccessId, filters } = config ?? {}

      return (
        <EmployeeSelectorV2
          label={label}
          value={value}
          onChange={(state) => {
            onChange(state)
          }}
          options={options}
          forcedFilters={forcedFilters}
          disabled={disabled}
          hint={hint}
          messages={{ placeholder, ...messages }}
          useAccessId={useAccessId}
          filters={filters}
        />
      )
    },
  })
}

const createMandatorySingleSchema = () => z.string()

const mandatorySingleEmployeeSelectorV2FormField = ({
  label,
  section,
  helpText,
  fieldConfig,
  renderIf,
}: BaseEmployeeSelectorV2FormFieldParams) => {
  const schema = createMandatorySingleSchema()

  return f0FormField<typeof schema, FieldConfig>(schema, {
    label,
    section,
    helpText,
    fieldType: 'custom',
    fieldConfig: fieldConfig ?? {},
    renderIf,
    render: ({
      value,
      onChange,
      disabled,
      placeholder,
      config,
    }: CustomFieldRenderProps<string, FieldConfig>) => {
      const { forcedFilters, options, hint, messages, useAccessId, filters } = config ?? {}

      return (
        <EmployeeSelectorV2
          label={label}
          value={value}
          onChange={(state) => {
            if (!state) return
            onChange(state)
          }}
          options={options}
          forcedFilters={forcedFilters}
          disabled={disabled}
          hint={hint}
          messages={{ placeholder, ...messages }}
          useAccessId={useAccessId}
          filters={filters}
          mandatory
        />
      )
    },
  })
}

const createOptionalMultiSchema = () =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('manual'),
      ids: z.array(z.string()),
    }),
    z.object({
      type: z.literal('all'),
      filters: z.record(z.string(), z.unknown()).optional(),
      excludedIds: z.array(z.string()),
      includeInactive: z.boolean().optional(),
    }),
  ])

const optionalMultiEmployeeSelectorV2FormField = ({
  label,
  section,
  helpText,
  fieldConfig,
  renderIf,
}: BaseEmployeeSelectorV2FormFieldParams) => {
  const schema = createOptionalMultiSchema()

  return f0FormField<typeof schema, FieldConfig>(schema, {
    label,
    section,
    helpText,
    fieldType: 'custom',
    fieldConfig: fieldConfig ?? {},
    renderIf,
    render: ({
      value,
      placeholder,
      onChange,
      disabled,
      config,
    }: CustomFieldRenderProps<z.infer<typeof schema>, FieldConfig>) => {
      const { forcedFilters, options, hint, messages, useAccessId, filters } = config ?? {}

      const valueToPass = value?.type === 'manual' ? value.ids : []

      return (
        <EmployeeSelectorV2
          multiple
          label={label}
          value={valueToPass}
          onChange={onChange}
          options={options}
          forcedFilters={forcedFilters}
          disabled={disabled}
          hint={hint}
          messages={{ placeholder, ...messages }}
          useAccessId={useAccessId}
          filters={filters}
        />
      )
    },
  })
}

const createMandatoryMultiSchema = () =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('manual'),
      ids: z.array(z.string()).min(1),
    }),
    z.object({
      type: z.literal('all'),
      filters: z.record(z.string(), z.unknown()).optional(),
      excludedIds: z.array(z.string()),
      includeInactive: z.boolean().optional(),
    }),
  ])

const mandatoryMultiEmployeeSelectorV2FormField = ({
  label,
  section,
  helpText,
  fieldConfig,
  renderIf,
}: BaseEmployeeSelectorV2FormFieldParams) => {
  const schema = createMandatoryMultiSchema()

  return f0FormField<typeof schema, FieldConfig>(schema, {
    label,
    section,
    helpText,
    fieldType: 'custom',
    fieldConfig: fieldConfig ?? {},
    renderIf,
    render: ({
      value,
      onChange,
      disabled,
      placeholder,
      config,
    }: CustomFieldRenderProps<z.infer<typeof schema>, FieldConfig>) => {
      const { forcedFilters, options, hint, messages, useAccessId, filters } = config ?? {}

      const valueToPass = value?.type === 'manual' ? value.ids : []

      return (
        <EmployeeSelectorV2
          multiple
          value={valueToPass}
          label={label}
          onChange={onChange}
          options={options}
          forcedFilters={forcedFilters}
          disabled={disabled}
          hint={hint}
          messages={{ placeholder, ...messages }}
          useAccessId={useAccessId}
          filters={filters}
          mandatory
        />
      )
    },
  })
}

type SingleEmployeeSelectorV2FormFieldParams<M extends boolean> =
  BaseEmployeeSelectorV2FormFieldParams & { mandatory: M }

export const singleEmployeeSelectorV2FormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: SingleEmployeeSelectorV2FormFieldParams<M>) => {
  return mandatory
    ? mandatorySingleEmployeeSelectorV2FormField({ ...rest, renderIf })
    : optionalSingleEmployeeSelectorV2FormField({ ...rest, renderIf })
}

type MultiEmployeeSelectorV2FormFieldParams<M extends boolean> =
  BaseEmployeeSelectorV2FormFieldParams & { mandatory: M }

export const multiEmployeeSelectorV2FormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiEmployeeSelectorV2FormFieldParams<M>) => {
  return mandatory
    ? mandatoryMultiEmployeeSelectorV2FormField({ ...rest, renderIf })
    : optionalMultiEmployeeSelectorV2FormField({ ...rest, renderIf })
}
